import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/i18nContext';
import Button from '../../components/ui/Button';
import '../../styles/AdminEntity.css';

const STATUSES = ['draft', 'active', 'suspended', 'expired', 'terminated'];
const BILLING_CYCLES = ['monthly', 'quarterly', 'yearly'];
const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];

const EMPTY_FORM = {
  customer_id: '', property_id: '', status: 'draft',
  start_date: '', end_date: '', auto_renew: false, notice_period_days: '',
  visits_per_week: 1, visit_weekdays: [], visit_duration_min: '', ticket_lead_days: 2,
  fee_amount: '', fee_currency: 'EUR', billing_cycle: '', billing_day: '', payment_method: '',
  scope_description: '', notes: '', signed_at: '',
};

const fromCsv = (csv) => (csv ? String(csv).split(',').map(Number) : []);
const toCsv = (arr) => (arr.length ? [...arr].sort((a, b) => a - b).join(',') : '');
const dateOnly = (v) => (v ? String(v).substring(0, 10) : '');

const Field = ({ form, onChange, label, name, type = 'text', hint, ...rest }) => (
  <div className="ae-field">
    <label>{label}</label>
    <input name={name} type={type} value={form[name] ?? ''} onChange={onChange} {...rest} />
    {hint && <small style={{ color: 'var(--text-muted)' }}>{hint}</small>}
  </div>
);

const ContractsPage = () => {
  const { api } = useAuth();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();

  // t() nem interpolál — a {{var}} helyettesítés hívási helyen történik
  const tf = (key, vars) =>
    Object.entries(vars).reduce((s, [k, v]) => s.replaceAll(`{{${k}}}`, v), t(key));

  const [contracts, setContracts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [msg, setMsg] = useState({ text: '', type: '' });

  const [editing, setEditing] = useState(null); // null = list, 'new' | id = form
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, pRes, uRes] = await Promise.all([
        api.get('/contracts'),
        api.get('/properties'),
        api.get('/admin/users'),
      ]);
      setContracts(cRes.data);
      setProperties(pRes.data);
      setUsers((uRes.data.users || []).filter(u => !u.deleted_at));
    } catch { setMsg({ text: t('contracts.loadFailed'), type: 'error' }); }
    finally { setLoading(false); }
  }, [api, t]);

  useEffect(() => { load(); }, [load]);

  const flash = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: '' }), 4000);
  };

  const openNew = () => { setForm(EMPTY_FORM); setEditing('new'); };

  const openEdit = useCallback(async (id) => {
    try {
      const r = await api.get(`/contracts/${id}`);
      const c = r.data;
      setForm({
        customer_id: c.customer_id ?? '',
        property_id: c.property_id ?? '',
        status: c.status ?? 'draft',
        start_date: dateOnly(c.start_date),
        end_date: dateOnly(c.end_date),
        auto_renew: !!c.auto_renew,
        notice_period_days: c.notice_period_days ?? '',
        visits_per_week: c.visits_per_week ?? 1,
        visit_weekdays: fromCsv(c.visit_weekdays),
        visit_duration_min: c.visit_duration_min ?? '',
        ticket_lead_days: c.ticket_lead_days ?? 2,
        fee_amount: c.fee_amount ?? '',
        fee_currency: c.fee_currency ?? 'EUR',
        billing_cycle: c.billing_cycle ?? '',
        billing_day: c.billing_day ?? '',
        payment_method: c.payment_method ?? '',
        scope_description: c.scope_description ?? '',
        notes: c.notes ?? '',
        signed_at: dateOnly(c.signed_at),
      });
      setEditing(id);
    } catch { flash(t('contracts.loadFailed'), 'error'); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, t]);

  // Deep-link: /admin/contracts?edit=:id
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && !loading) {
      openEdit(Number(editId));
      setSearchParams({}, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => {
      const next = { ...f, [name]: type === 'checkbox' ? checked : value };
      // Ügyfélváltáskor a korábbi ingatlan már nem érvényes választás
      if (name === 'customer_id') next.property_id = '';
      return next;
    });
  };

  const toggleWeekday = (d) => setForm(f => ({
    ...f,
    visit_weekdays: f.visit_weekdays.includes(d)
      ? f.visit_weekdays.filter(x => x !== d)
      : [...f.visit_weekdays, d],
  }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, visit_weekdays: toCsv(form.visit_weekdays) };
    try {
      if (editing === 'new') {
        await api.post('/contracts', payload);
        flash(t('contracts.created'));
      } else {
        await api.put(`/contracts/${editing}`, payload);
        flash(t('contracts.updated'));
      }
      setEditing(null);
      load();
    } catch (err) {
      flash(err.response?.data?.error || t('contracts.saveFailed'), 'error');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, number) => {
    if (!window.confirm(tf('contracts.deleteConfirm', { number }))) return;
    try {
      await api.delete(`/contracts/${id}`);
      setContracts(list => list.filter(x => x.id !== id));
      flash(t('contracts.deleted'));
    } catch (err) { flash(err.response?.data?.error || t('contracts.deleteFailed'), 'error'); }
  };

  // Az ingatlan oldaláról ideugorva csak az adott ingatlan szerződéseit mutatjuk
  const propertyFilter = searchParams.get('property_id');
  const filteredProperty = propertyFilter
    ? properties.find(p => String(p.id) === propertyFilter)
    : null;

  const filtered = contracts.filter(c =>
    (!propertyFilter || String(c.property_id) === propertyFilter) &&
    [c.contract_number, c.customer_username, c.customer_email, c.property_name]
      .join(' ').toLowerCase().includes(search.toLowerCase())
  );

  if (editing !== null) {
    // A backend elutasítja azt az ingatlant, ami nem a kiválasztott ügyfélé — a select is ezt tükrözze
    const customerProperties = form.customer_id
      ? properties.filter(p => String(p.user_id) === String(form.customer_id))
      : [];
    const selectedProperty = properties.find(p => String(p.id) === String(form.property_id));

    return (
      <div className="ae-page">
        {msg.text && <div className={`ae-msg ${msg.type}`}>{msg.text}</div>}
        <div className="ae-card">
          <div className="ae-form-header">
            <Button variant="ghost" size="sm" onClick={() => setEditing(null)}>← {t('admin.back') || 'Back'}</Button>
            <h2 className="ae-form-title">{editing === 'new' ? t('contracts.new') : t('contracts.edit')}</h2>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.65rem' }}>
              <Button variant="ghost" type="button" onClick={() => setEditing(null)}>{t('admin.cancel')}</Button>
              <Button variant="solid" type="submit" form="contract-form" disabled={saving}>
                {saving ? t('admin.saving') : t('admin.save')}
              </Button>
            </div>
          </div>

          <form id="contract-form" onSubmit={handleSave}>
            <div className="ae-form-body">

              <div className="ae-form-section">
                <p className="ae-form-section-title">{t('contracts.sectionBasic')}</p>
                <div className="ae-grid">
                  <div className="ae-field">
                    <label>{t('contracts.customer')} *</label>
                    <select name="customer_id" value={form.customer_id} onChange={handleChange} required>
                      <option value="">{t('contracts.selectCustomer')}</option>
                      {users.map(u => (
                        <option key={u.id} value={u.id}>{u.username} ({u.email})</option>
                      ))}
                    </select>
                  </div>
                  <div className="ae-field">
                    <label>{t('contracts.property')} *</label>
                    <select name="property_id" value={form.property_id} onChange={handleChange} required
                            disabled={!form.customer_id}>
                      <option value="">
                        {!form.customer_id
                          ? t('contracts.selectCustomerFirst')
                          : customerProperties.length === 0
                            ? t('contracts.noPropertyForCustomer')
                            : t('contracts.selectProperty')}
                      </option>
                      {customerProperties.map(p => (
                        <option key={p.id} value={p.id}>{p.name}{p.address_city ? ` — ${p.address_city}` : ''}</option>
                      ))}
                    </select>
                  </div>
                  <div className="ae-field">
                    <label>{t('contracts.status')}</label>
                    <select name="status" value={form.status} onChange={handleChange}>
                      {STATUSES.map(s => <option key={s} value={s}>{t(`contracts.statuses.${s}`)}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div className="ae-form-section">
                <p className="ae-form-section-title">{t('contracts.sectionTerm')}</p>
                <div className="ae-grid">
                  <Field form={form} onChange={handleChange} label={`${t('contracts.startDate')} *`} name="start_date" type="date" required />
                  <Field form={form} onChange={handleChange} label={t('contracts.endDate')} name="end_date" type="date" hint={t('contracts.endDateHint')} />
                  <Field form={form} onChange={handleChange} label={t('contracts.noticePeriodDays')} name="notice_period_days" type="number" min="0" />
                  <div className="ae-checkbox-row">
                    <input type="checkbox" id="chk-auto_renew" name="auto_renew" checked={!!form.auto_renew} onChange={handleChange} />
                    <label htmlFor="chk-auto_renew">{t('contracts.autoRenew')}</label>
                  </div>
                </div>
              </div>

              <div className="ae-form-section">
                <p className="ae-form-section-title">{t('contracts.sectionVisits')}</p>
                <div className="ae-grid">
                  <Field form={form} onChange={handleChange} label={`${t('contracts.visitsPerWeek')} *`} name="visits_per_week" type="number" min="1" max="14" required />
                  <Field form={form} onChange={handleChange} label={t('contracts.visitDurationMin')} name="visit_duration_min" type="number" min="0" />
                  <Field form={form} onChange={handleChange} label={t('contracts.ticketLeadDays')} name="ticket_lead_days" type="number" min="0" max="30" />
                </div>

                <div className="ae-field" style={{ marginTop: '0.75rem' }}>
                  <label>{t('contracts.visitWeekdays')}</label>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.35rem' }}>
                    {WEEKDAYS.map(d => {
                      const on = form.visit_weekdays.includes(d);
                      return (
                        <button key={d} type="button" onClick={() => toggleWeekday(d)}
                          aria-pressed={on}
                          style={{
                            minWidth: 46, padding: '0.4rem 0.6rem', cursor: 'pointer',
                            borderRadius: 6, fontWeight: 600, fontSize: '0.82rem',
                            border: `1px solid ${on ? 'var(--color-primary)' : 'var(--border-color)'}`,
                            background: on ? 'var(--color-primary)' : 'transparent',
                            color: on ? '#fff' : 'var(--text-primary)',
                          }}>
                          {t(`contracts.weekdays.${d}`)}
                        </button>
                      );
                    })}
                  </div>
                  <small style={{ color: 'var(--text-muted)' }}>{t('contracts.visitWeekdaysHint')}</small>
                  {form.visit_weekdays.length > Number(form.visits_per_week || 0) && (
                    <small style={{ color: 'var(--error-text)' }}>
                      {form.visit_weekdays.length} &gt; {form.visits_per_week}
                    </small>
                  )}
                </div>

                {selectedProperty && (
                  <div className={`ae-msg ${selectedProperty.preferred_agent_username ? 'success' : 'warning'}`}
                       style={{ marginTop: '0.75rem' }}>
                    {selectedProperty.preferred_agent_username
                      ? tf('contracts.agentInfo', { agent: selectedProperty.preferred_agent_username })
                      : t('contracts.agentMissing')}
                  </div>
                )}
              </div>

              <div className="ae-form-section">
                <p className="ae-form-section-title">{t('contracts.sectionBilling')}</p>
                <div className="ae-grid">
                  <Field form={form} onChange={handleChange} label={t('contracts.feeAmount')} name="fee_amount" type="number" step="0.01" min="0" />
                  <Field form={form} onChange={handleChange} label={t('contracts.feeCurrency')} name="fee_currency" maxLength={5} />
                  <div className="ae-field">
                    <label>{t('contracts.billingCycle')}</label>
                    <select name="billing_cycle" value={form.billing_cycle} onChange={handleChange}>
                      <option value="">{t('contracts.cycles.none')}</option>
                      {BILLING_CYCLES.map(c => <option key={c} value={c}>{t(`contracts.cycles.${c}`)}</option>)}
                    </select>
                  </div>
                  <Field form={form} onChange={handleChange} label={t('contracts.billingDay')} name="billing_day" type="number" min="1" max="31" />
                  <Field form={form} onChange={handleChange} label={t('contracts.paymentMethod')} name="payment_method" />
                </div>
              </div>

              <div className="ae-form-section">
                <p className="ae-form-section-title">{t('contracts.sectionOther')}</p>
                <div className="ae-grid">
                  <Field form={form} onChange={handleChange} label={t('contracts.signedAt')} name="signed_at" type="date" />
                </div>
                <div className="ae-field">
                  <label>{t('contracts.scopeDescription')}</label>
                  <textarea name="scope_description" value={form.scope_description} onChange={handleChange} rows={3} />
                </div>
                <div className="ae-field">
                  <label>{t('contracts.notes')}</label>
                  <textarea name="notes" value={form.notes} onChange={handleChange} rows={3} />
                </div>
              </div>

            </div>
            <div className="ae-form-footer">
              <Button variant="ghost" type="button" onClick={() => setEditing(null)}>{t('admin.cancel')}</Button>
              <Button variant="solid" type="submit" disabled={saving}>
                {saving ? t('admin.saving') : t('admin.save')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="ae-page">
      {msg.text && <div className={`ae-msg ${msg.type}`}>{msg.text}</div>}

      <div className="ae-card ae-header-card">
        <div className="ae-topbar">
          <h1 className="ae-title">{t('contracts.title')}</h1>
          <Button variant="solid" onClick={openNew}>{t('contracts.add')}</Button>
        </div>
        <div className="ae-filters">
          <input className="ae-search" placeholder={t('contracts.search')}
                 value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        {propertyFilter && (
          <div className="ae-msg" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span>
              {tf('contracts.filteredByProperty', {
                property: filteredProperty?.name || `#${propertyFilter}`,
              })}
            </span>
            <Button variant="ghost" size="sm" onClick={() => setSearchParams({}, { replace: true })}>
              {t('contracts.clearFilter')}
            </Button>
          </div>
        )}
      </div>

      <div className="ae-card">
        {loading ? (
          <div className="ae-empty">{t('common.loading')}</div>
        ) : filtered.length === 0 ? (
          <div className="ae-empty">{t('contracts.none')}</div>
        ) : (
          <div className="ae-table-wrap">
            <table className="ae-table">
              <thead>
                <tr>
                  <th>{t('contracts.number')}</th>
                  <th>{t('contracts.customer')}</th>
                  <th>{t('contracts.property')}</th>
                  <th>{t('contracts.status')}</th>
                  <th>{t('contracts.term')}</th>
                  <th>{t('contracts.sectionVisits')}</th>
                  <th>{t('contracts.feeAmount')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => (
                  <tr key={c.id}>
                    <td className="ae-name">{c.contract_number}</td>
                    <td>{c.customer_username || c.customer_email}</td>
                    <td>{c.property_name || '—'}</td>
                    <td>
                      <span className={`ae-badge ${c.status === 'active' ? 'active' : 'inactive'}`}>
                        {t(`contracts.statuses.${c.status}`)}
                      </span>
                    </td>
                    <td>
                      {dateOnly(c.start_date)} → {dateOnly(c.end_date) || t('contracts.openEnded')}
                    </td>
                    <td>
                      {tf('contracts.weekly', { count: c.visits_per_week })}
                      {c.visit_weekdays && (
                        <span style={{ color: 'var(--text-muted)' }}>
                          {' '}({fromCsv(c.visit_weekdays).map(d => t(`contracts.weekdays.${d}`)).join(', ')})
                        </span>
                      )}
                    </td>
                    <td>{c.fee_amount ? `${c.fee_amount} ${c.fee_currency}` : '—'}</td>
                    <td>
                      <div className="ae-actions">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(c.id)}>{t('admin.edit')}</Button>
                        <Button variant="danger" size="sm" onClick={() => handleDelete(c.id, c.contract_number)}>{t('admin.delete')}</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContractsPage;
